import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, CheckCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import BrandedHeader from "@/components/BrandedHeader";

export default function CandidateLoginPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  const handleRequestMagicLink = async (e) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/candidate/request-magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setSent(true);
        toast.success("Magic link sent! Check your email.");
        
        // Start 60-second countdown for resend
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        toast.error(data.detail || "Failed to send magic link");
      }
    } catch (error) {
      console.error("Magic link request failed:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setSent(false);
    setEmail("");
    setCountdown(0);
  };

  return (
    <div className="min-h-screen bg-background">
      <BrandedHeader>
        <h1 className="text-lg font-display font-semibold text-center">Candidate Portal</h1>
      </BrandedHeader>

      <div className="container mx-auto px-4 py-12 max-w-md">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              {sent ? (
                <CheckCircle className="w-8 h-8 text-primary" />
              ) : (
                <Mail className="w-8 h-8 text-primary" />
              )}
            </div>
            <CardTitle className="text-2xl font-display">
              {sent ? "Check Your Email" : "Sign In to Your Dashboard"}
            </CardTitle>
          </CardHeader>

          <CardContent>
            {!sent ? (
              <form onSubmit={handleRequestMagicLink} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="candidate@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full"
                    data-testid="candidate-email-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Enter your registered email address
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="send-magic-link-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Magic Link"
                  )}
                </Button>

                <div className="text-center text-sm text-muted-foreground mt-4">
                  <span className="inline-flex items-center gap-2">
                    🔒 We'll email you a secure login link
                  </span>
                </div>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 text-sm">
                  <p className="font-medium mb-2">We've sent a secure login link to:</p>
                  <p className="text-primary font-mono">{email}</p>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <p className="flex items-start gap-2">
                    <span>📬</span>
                    <span>Click the link in your email to access your dashboard. It expires in 15 minutes.</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span>📧</span>
                    <span>Check your spam folder if you don't see it.</span>
                  </p>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-3">Didn't receive it?</p>
                  {countdown > 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Resend available in {countdown}s
                    </p>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleResend}
                      className="w-full"
                      data-testid="resend-link-btn"
                    >
                      Resend Link
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Need help? Contact{" "}
            <a href="mailto:careers@securado.com" className="text-primary hover:underline">
              careers@securado.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
